import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Award, 
  TrendingUp, 
  FileText, 
  Download, 
  Eye, 
  EyeOff,
  ChevronDown,
  ChevronRight,
  Star,
  AlertTriangle,
  CheckCircle,
  Target
} from 'lucide-react';
import { Card, Button } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { TenderAnalysisState, InitialReviewResult } from '../types';

const ResultContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: ${theme.spacing[6]};
`;

const ResultHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
`;

const ResultTitle = styled.h1`
  margin-bottom: ${theme.spacing[4]};
  color: ${theme.colors.textPrimary};
`;

const ScoreCard = styled(Card)<{ $score: number }>`
  padding: ${theme.spacing[8]};
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
  background: ${({ $score }) => {
    if ($score >= 80) return `linear-gradient(135deg, ${theme.colors.success[100]}, ${theme.colors.success[100]}50)`;
    if ($score >= 60) return `linear-gradient(135deg, ${theme.colors.warning[100]}, ${theme.colors.warning[100]}50)`;
    return `linear-gradient(135deg, ${theme.colors.error[100]}, ${theme.colors.error[100]}50)`;
  }};
  border-color: ${({ $score }) => {
    if ($score >= 80) return theme.colors.success[500];
    if ($score >= 60) return theme.colors.warning[500];
    return theme.colors.error[500];
  }};
`;

const ScoreNumber = styled.div<{ $score: number }>`
  font-size: 4rem;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ $score }) => {
    if ($score >= 80) return theme.colors.success[600];
    if ($score >= 60) return theme.colors.warning[600];
    return theme.colors.error[600];
  }};
  margin-bottom: ${theme.spacing[2]};
`;

const ScoreLabel = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[4]};
`;

const ScoreDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textPrimary};
  line-height: ${theme.typography.lineHeight.relaxed};
  max-width: 600px;
  margin: 0 auto;
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
`;

const SectionCard = styled(Card)`
  padding: ${theme.spacing[6]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  cursor: pointer;
  user-select: none;
`;

const SectionIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
`;

const SectionTitle = styled.h3`
  flex: 1;
  color: ${theme.colors.textPrimary};
`;

const SectionContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${({ $isExpanded }) => $isExpanded ? 'none' : '0'};
  overflow: hidden;
  transition: all ${theme.transitions.base};
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const QuestionItem = styled.div`
  padding: ${theme.spacing[3]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.primary[500]};
`;

const QuestionText = styled.p`
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[2]};
  color: ${theme.colors.textPrimary};
`;

const QuestionMeta = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
`;

const ImprovementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ImprovementItem = styled.div<{ $priority: 'high' | 'medium' | 'low' }>`
  display: flex;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${({ $priority }) => {
    switch ($priority) {
      case 'high': return theme.colors.error[500];
      case 'medium': return theme.colors.warning[500];
      case 'low': return theme.colors.info[500];
      default: return theme.colors.primary[300];
    }
  }};
`;

const ImprovementIcon = styled.div<{ $priority: 'high' | 'medium' | 'low' }>`
  color: ${({ $priority }) => {
    switch ($priority) {
      case 'high': return theme.colors.error[600];
      case 'medium': return theme.colors.warning[600];
      case 'low': return theme.colors.info[600];
      default: return theme.colors.primary[500];
    }
  }};
`;

const ImprovementContent = styled.div`
  flex: 1;
`;

const ImprovementTitle = styled.h4`
  margin-bottom: ${theme.spacing[1]};
  color: ${theme.colors.textPrimary};
`;

const ImprovementDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: center;
  margin-top: ${theme.spacing[8]};
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const ReportContent = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[6]};
  margin-top: ${theme.spacing[6]};
  white-space: pre-wrap;
  font-family: ${theme.typography.fontFamily.mono};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  max-height: 400px;
  overflow-y: auto;
`;

interface FinalResultProps {
  analysisData: TenderAnalysisState;
  onDownloadReport?: () => void;
  onStartNewAnalysis?: () => void;
}

const FinalResult: React.FC<FinalResultProps> = ({ 
  analysisData, 
  onDownloadReport,
  onStartNewAnalysis 
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['score']));
  const [showFullReport, setShowFullReport] = useState(false);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return theme.colors.success;
    if (score >= 60) return theme.colors.warning;
    return theme.colors.error;
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Improvement';
  };

  // Phase A — improvement suggestions are now sourced from the grounded
  // backend pipeline (initial_score_report.improvement_areas). No mocks;
  // when the backend hasn't produced any, we render an empty state instead
  // of fabricating "Enhance Technical Specifications" boilerplate.
  const improvementAreas: string[] = (analysisData as any)?.initial_score_report?.improvement_areas || [];
  const realImprovements = improvementAreas.map((text: string, idx: number) => ({
    id: `improvement-${idx}`,
    title: text.split(/[:.\n]/)[0].slice(0, 80) || `Improvement ${idx + 1}`,
    description: text,
    // Priority is heuristic only when the backend doesn't tag — we map by
    // position (highest-priority area first in the rubric output).
    priority: (idx === 0 ? 'high' : idx <= 2 ? 'medium' : 'low') as 'high' | 'medium' | 'low',
  }));

  // Phase A — score: prefer the structured initial_score_report.overall_score,
  // then fall back to legacy initial_review_result.score. Render null as
  // "—" rather than silently zero.
  const rawScore: number | null =
    typeof (analysisData as any)?.initial_score_report?.overall_score === 'number'
      ? (analysisData as any).initial_score_report.overall_score
      : (typeof analysisData.initial_review_result?.score === 'number'
          ? analysisData.initial_review_result.score
          : null);
  const score: number = typeof rawScore === 'number' ? rawScore : 0;
  const scoreUnknown = rawScore === null;

  return (
    <ResultContainer>
      <ResultHeader>
        <ResultTitle>Analysis Results</ResultTitle>
      </ResultHeader>

      <ScoreCard $score={score}>
        <ScoreNumber $score={score}>{scoreUnknown ? '—' : score}</ScoreNumber>
        <ScoreLabel>{scoreUnknown ? 'Score pending' : `${getScoreLabel(score)} Score`}</ScoreLabel>
        <ScoreDescription>
          {analysisData.initial_review_result?.review ||
            (scoreUnknown
              ? 'The Initial Assessment hasn’t produced a score yet. Run or rerun that step to populate this view.'
              : 'Your tender response has been analyzed and scored based on completeness, clarity, and alignment with requirements.')}
        </ScoreDescription>
      </ScoreCard>

      <SectionsGrid>
        <SectionCard>
          <SectionHeader onClick={() => toggleSection('questions')}>
            <SectionIcon $color={theme.colors.primary[500]}>
              <Target size={20} />
            </SectionIcon>
            <SectionTitle>Questions Identified ({analysisData.questions?.length || 0})</SectionTitle>
            {expandedSections.has('questions') ? <ChevronDown /> : <ChevronRight />}
          </SectionHeader>
          <SectionContent $isExpanded={expandedSections.has('questions')}>
            <QuestionsList>
              {analysisData.questions?.slice(0, 3).map((question, index) => (
                <QuestionItem key={index}>
                  <QuestionText>{question.text || `Question ${index + 1}`}</QuestionText>
                  <QuestionMeta>
                    <span>Type: {question.type || 'General'}</span>
                    <span>Section: {question.section || 'Main'}</span>
                  </QuestionMeta>
                </QuestionItem>
              )) || [
                <QuestionItem key="placeholder">
                  <QuestionText>Questions will appear here after analysis</QuestionText>
                </QuestionItem>
              ]}
            </QuestionsList>
          </SectionContent>
        </SectionCard>

        <SectionCard>
          <SectionHeader onClick={() => toggleSection('criteria')}>
            <SectionIcon $color={theme.colors.info[500]}>
              <CheckCircle size={20} />
            </SectionIcon>
            <SectionTitle>Evaluation Criteria ({analysisData.evaluation_criteria?.length || 0})</SectionTitle>
            {expandedSections.has('criteria') ? <ChevronDown /> : <ChevronRight />}
          </SectionHeader>
          <SectionContent $isExpanded={expandedSections.has('criteria')}>
            <QuestionsList>
              {analysisData.evaluation_criteria?.slice(0, 3).map((criteria, index) => (
                <QuestionItem key={index}>
                  <QuestionText>{criteria.name || `Criteria ${index + 1}`}</QuestionText>
                  <QuestionMeta>
                    <span>Weight: {criteria.weight || 0}%</span>
                    <span>Method: {criteria.scoring_method || 'Standard'}</span>
                  </QuestionMeta>
                </QuestionItem>
              )) || [
                <QuestionItem key="placeholder">
                  <QuestionText>Evaluation criteria will appear here after analysis</QuestionText>
                </QuestionItem>
              ]}
            </QuestionsList>
          </SectionContent>
        </SectionCard>

        <SectionCard>
          <SectionHeader onClick={() => toggleSection('improvements')}>
            <SectionIcon $color={theme.colors.warning[500]}>
              <TrendingUp size={20} />
            </SectionIcon>
            <SectionTitle>Improvement Suggestions</SectionTitle>
            {expandedSections.has('improvements') ? <ChevronDown /> : <ChevronRight />}
          </SectionHeader>
          <SectionContent $isExpanded={expandedSections.has('improvements')}>
            {realImprovements.length === 0 ? (
              <div style={{ color: theme.colors.textMuted, fontSize: theme.typography.fontSize.sm, padding: theme.spacing[2] }}>
                No improvement areas have been generated yet. Run the Initial Assessment step to populate suggestions grounded in your tender content.
              </div>
            ) : (
              <ImprovementsList>
                {realImprovements.map((improvement) => (
                  <ImprovementItem key={improvement.id} $priority={improvement.priority}>
                    <ImprovementIcon $priority={improvement.priority}>
                      {improvement.priority === 'high' && <AlertTriangle size={16} />}
                      {improvement.priority === 'medium' && <Star size={16} />}
                      {improvement.priority === 'low' && <Target size={16} />}
                    </ImprovementIcon>
                    <ImprovementContent>
                      <ImprovementTitle>{improvement.title}</ImprovementTitle>
                      <ImprovementDescription>{improvement.description}</ImprovementDescription>
                    </ImprovementContent>
                  </ImprovementItem>
                ))}
              </ImprovementsList>
            )}
          </SectionContent>
        </SectionCard>

        <SectionCard>
          <SectionHeader onClick={() => toggleSection('report')}>
            <SectionIcon $color={theme.colors.success[500]}>
              <FileText size={20} />
            </SectionIcon>
            <SectionTitle>Detailed Report</SectionTitle>
            {expandedSections.has('report') ? <ChevronDown /> : <ChevronRight />}
          </SectionHeader>
          <SectionContent $isExpanded={expandedSections.has('report')}>
            <Button
              $variant="outline"
              $size="sm"
              onClick={() => setShowFullReport(!showFullReport)}
              style={{ marginBottom: theme.spacing[4] }}
            >
              {showFullReport ? <EyeOff size={16} /> : <Eye size={16} />}
              {showFullReport ? 'Hide' : 'Show'} Full Report
            </Button>
            {showFullReport && (
              <ReportContent>
                {analysisData.final_report || 
                 analysisData.comparison_content || 
                 'Detailed analysis report will be available here after completion.'}
              </ReportContent>
            )}
          </SectionContent>
        </SectionCard>
      </SectionsGrid>

      <ActionsBar>
        {onDownloadReport && (
          <Button $variant="outline" onClick={onDownloadReport}>
            <Download size={20} />
            Download Report
          </Button>
        )}
        {onStartNewAnalysis && (
          <Button onClick={onStartNewAnalysis}>
            <FileText size={20} />
            Analyze Another Document
          </Button>
        )}
      </ActionsBar>
    </ResultContainer>
  );
};

export default FinalResult;